import { useState } from 'react'
import { useSupabaseQuery } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'

export default function RequestsTab({ selectedAccount }) {
  const [selectedRequest, setSelectedRequest] = useState(null)

  const { data: requests, loading, refetch } = useSupabaseQuery('OTPRequests', {
    eq: { ...(selectedAccount && { AccountId: selectedAccount.AccountId }) },
    order: { column: 'CreatedUtc', ascending: false },
    limit: 100
  })

  if (!selectedAccount) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Account Selected</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Please select an account from the Accounts tab first
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">OTP Request Log</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            OTP request history for: {selectedAccount.Name}
          </p>
        </div>
        <button
          onClick={refetch}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {requests && requests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Channel</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Target</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Expires</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Validated</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {requests.map((req) => (
                  <tr key={req.RequestId} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                    <td className="px-4 py-4 whitespace-nowrap">{getChannelBadge(req.Channel)}</td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-mono text-gray-900 dark:text-white max-w-xs truncate">
                        {req.Msisdn || req.Email || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(req.Status, req.ExpiresUtc)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(req.CreatedUtc).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(req.ExpiresUtc).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {req.ValidatedUtc ? new Date(req.ValidatedUtc).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setSelectedRequest(req)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No OTP requests yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              OTP request history will appear here once you start issuing codes.
            </p>
          </div>
        )}
      </div>

      <Modal
        isOpen={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        title="OTP Request Details"
        size="lg"
      >
        {selectedRequest && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Request ID</label>
                <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">{selectedRequest.RequestId}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Channel</label>
                <p className="mt-1">{getChannelBadge(selectedRequest.Channel)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</label>
                <p className="mt-1">{getStatusBadge(selectedRequest.Status, selectedRequest.ExpiresUtc)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Request Source</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">{selectedRequest.RequestSource || '—'}</p>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Target</label>
                <p className="text-sm font-mono text-gray-900 dark:text-white mt-1 break-all">
                  {selectedRequest.Msisdn || selectedRequest.Email || '—'}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Code Length</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">{selectedRequest.UserLength} digits</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expiry (min)</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">{selectedRequest.UserExpiry}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">{new Date(selectedRequest.CreatedUtc).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expires</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">{new Date(selectedRequest.ExpiresUtc).toLocaleString()}</p>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Validated</label>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {selectedRequest.ValidatedUtc ? new Date(selectedRequest.ValidatedUtc).toLocaleString() : 'Not validated'}
                </p>
              </div>
              {selectedRequest.Meta && (
                <div className="col-span-2">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Meta</label>
                  <pre className="text-xs font-mono text-gray-600 dark:text-gray-400 mt-1 bg-gray-50 dark:bg-gray-900 rounded p-3 overflow-x-auto">
                    {JSON.stringify(selectedRequest.Meta, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

function getChannelBadge(channel) {
  const c = String(channel || '').toLowerCase()
  const label = String(channel || 'Unknown')
  const cls = c.includes('sms')
    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
    : c.includes('email')
      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200'
      : c.includes('whatsapp')
        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
  return <span className={`px-2 py-1 text-xs font-medium rounded ${cls}`}>{label}</span>
}

function getStatusBadge(status, expiresUtc) {
  const s = String(status || '').toLowerCase()
  const isExpired = expiresUtc && new Date(expiresUtc) < new Date() && s === 'pending'
  const effective = isExpired ? 'expired' : s

  if (effective === 'validated') {
    return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">Validated</span>
  }
  if (effective === 'expired') {
    return <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded-full">Expired</span>
  }
  if (effective === 'failed' || effective === 'invalid') {
    return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">Failed</span>
  }
  if (effective === 'pending') {
    return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full">Pending</span>
  }
  return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded-full capitalize">{status}</span>
}
